'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const API = 'https://app.trip-planner.com.au/api/caravan-leads';

const CATEGORIES = [
  { value: 'offroad_hybrid', label: 'Off-road / hybrid' },
  { value: 'family_van', label: 'Family van' },
  { value: 'couples_tourer', label: 'Couples tourer' },
  { value: 'camper_trailer', label: 'Camper trailer' },
  { value: 'unsure', label: 'Not sure yet' },
];
const BUDGETS = [
  { value: 'under_30k', label: 'Under $30k' },
  { value: '30_60k', label: '$30k – $60k' },
  { value: '60_100k', label: '$60k – $100k' },
  { value: '100k_plus', label: '$100k+' },
  { value: 'unsure', label: 'Still working it out' },
];
const TIMELINES = [
  { value: 'now', label: 'Ready now' },
  { value: '3_months', label: 'Within 3 months' },
  { value: '6_months', label: 'Within 6 months' },
  { value: '12_months', label: 'Within a year' },
  { value: 'researching', label: 'Just researching' },
];
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

const field =
  'w-full rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2.5 text-[14.5px] text-white placeholder:text-white/35 outline-none transition focus:border-[#63b3ed] focus:bg-white/[0.09] focus:ring-2 focus:ring-[#63b3ed]/40';
const label = 'block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/55 mb-1.5';

// Visual + AT cue for required fields, so "required" never relies on the
// reader inferring it from the absence of an "(optional)" suffix.
function Required() {
  // Matches the red already used for the error banner below — legible
  // against the dark form background, unlike the flat brand trail-red.
  return (
    <span className="text-red-300" aria-hidden="true"> *</span>
  );
}

function Segmented({
  options, value, onChange, name, required,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  name: string;
  required?: boolean;
}) {
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));

  // ARIA APG radiogroup: arrow keys move both focus and selection between
  // radios, wrapping at the ends; only one radio sits in the tab sequence.
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    if (next === -1) return;
    e.preventDefault();
    onChange(options[next].value);
    btnRefs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={name} aria-required={required || undefined} className="flex flex-wrap gap-2">
      {options.map((o, i) => (
        <button
          key={o.value}
          ref={(el) => { btnRefs.current[i] = el; }}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          tabIndex={i === selectedIndex ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKeyDown(e, i)}
          className={[
            'inline-flex items-center justify-center min-h-[44px] rounded-full px-4 text-[13px] font-semibold transition border',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#63b3ed]',
            value === o.value
              ? 'bg-[#1a68d1] border-[#1a68d1] text-white'
              : 'border-white/20 text-white/75 hover:border-white/45 hover:text-white',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function CaravanMatchForm({ brands }: { brands: string[] }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', state: '',
    category: '', budget: '', timeline: '',
    tow_vehicle: '', message: '', website_url: '',
  });
  const [brandPicks, setBrandPicks] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Status swaps the whole form for a confirmation block — move focus there so
  // screen-reader users get the confirmation even though nothing was clicked
  // to navigate them there (role="status" below covers the announcement itself).
  useEffect(() => {
    if (status === 'done') successHeadingRef.current?.focus();
  }, [status]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.category || !form.budget || !form.timeline) {
      setError('Pick a van type, budget and timeframe so we can match you properly.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          brands: brandPicks,
          source_page: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Something went wrong — please try again.');
      setStatus('done');
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      w.gtag?.('event', 'generate_lead', { form: 'caravan_match', category: form.category, budget: form.budget });
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong — please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div className="text-center py-10" role="status">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#1a68d1]/20 text-[#63b3ed]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5 5L20 6.5" /></svg>
        </div>
        <h3 ref={successHeadingRef} tabIndex={-1} className="font-heading mt-5 text-2xl text-white">You&apos;re on the list.</h3>
        <p className="mx-auto mt-2 max-w-md text-[14.5px] leading-relaxed text-white/70">
          We&apos;ll match your details with the right manufacturers and they&apos;ll be in touch
          with pricing and availability. Meanwhile, two smart next steps:
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link href="/apps/campsite-explorer/caravan-finance" className="rounded-xl bg-[#1a68d1] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">
            Work out repayments
          </Link>
          <Link href="/apps/campsite-explorer/?plan=1" className="rounded-xl border border-white/25 px-5 py-2.5 text-sm font-semibold text-white/90 transition hover:bg-white/10">
            Plan the first trip
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6">
      <div>
        <span className={label}>What are you after?<Required /></span>
        <Segmented options={CATEGORIES} value={form.category} onChange={set('category')} name="Van type" required />
      </div>
      <div>
        <span className={label}>Budget<Required /></span>
        <Segmented options={BUDGETS} value={form.budget} onChange={set('budget')} name="Budget" required />
      </div>
      <div>
        <span className={label}>When are you buying?<Required /></span>
        <Segmented options={TIMELINES} value={form.timeline} onChange={set('timeline')} name="Timeframe" required />
      </div>

      {brands.length > 0 && (
        <div>
          <span className={label}>Brands you&apos;d like to hear from (optional)</span>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => {
              const on = brandPicks.includes(b);
              return (
                <button
                  key={b}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setBrandPicks((p) => (on ? p.filter((x) => x !== b) : [...p, b]))}
                  className={[
                    'inline-flex items-center justify-center min-h-[44px] rounded-full px-4 text-[13px] font-semibold transition border',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#63b3ed]',
                    on ? 'bg-white text-[#1c1e20] border-white' : 'border-white/20 text-white/75 hover:border-white/45 hover:text-white',
                  ].join(' ')}
                >
                  {b}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor="cv-name">Name<Required /></label>
          <input id="cv-name" required maxLength={120} className={field} placeholder="Your name"
            value={form.name} onChange={(e) => set('name')(e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor="cv-email">Email<Required /></label>
          <input id="cv-email" required type="email" maxLength={200} className={field} placeholder="you@example.com"
            value={form.email} onChange={(e) => set('email')(e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor="cv-phone">Phone (optional)</label>
          <input id="cv-phone" maxLength={40} className={field} placeholder="04xx xxx xxx"
            value={form.phone} onChange={(e) => set('phone')(e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor="cv-state">State (optional)</label>
          <select id="cv-state" className={field} value={form.state} onChange={(e) => set('state')(e.target.value)}>
            <option value="" className="text-[#121212]">Choose…</option>
            {STATES.map((s) => <option key={s} value={s} className="text-[#121212]">{s}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={label} htmlFor="cv-tow">Tow vehicle (optional — helps match weight)</label>
        <input id="cv-tow" maxLength={120} className={field} placeholder="e.g. Ford Ranger, LandCruiser 300"
          value={form.tow_vehicle} onChange={(e) => set('tow_vehicle')(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor="cv-msg">Anything else? (optional)</label>
        <textarea id="cv-msg" rows={3} maxLength={2000} className={field}
          placeholder="Family of four, mostly east-coast trips with some gravel…"
          value={form.message} onChange={(e) => set('message')(e.target.value)} />
      </div>

      {/* Honeypot — hidden from humans, irresistible to bots */}
      <input type="text" name="website_url" tabIndex={-1} autoComplete="off" aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
        value={form.website_url} onChange={(e) => set('website_url')(e.target.value)} />

      {status === 'error' && (
        <p role="alert" className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-2.5 text-[13.5px] text-red-200">{error}</p>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <button type="submit" disabled={status === 'sending'}
          className="rounded-xl bg-[#1a68d1] px-7 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
          {status === 'sending' ? 'Sending…' : 'Get matched'}
        </button>
        <p className="text-[12px] leading-snug text-white/45 max-w-sm">
          We share your enquiry only with the manufacturers you&apos;d expect to hear from.
          No spam, no reselling your details.
        </p>
      </div>
    </form>
  );
}
